import PropTypes from 'prop-types';
import { useRef, useState, useEffect, useCallback } from 'react';
import SignatureOtpPanel from '@heyjob-dash/shared/components/signature-otp-panel';

import { Box, Card, Alert, Stack, Button, Dialog, Collapse, TextField, CardHeader, Typography, CardContent, DialogTitle, DialogContent, DialogActions } from '@mui/material';

import OnboardingService from '../../services/OnboardingService';

const message = (error) => (typeof error.response?.data?.message === 'string' ? error.response.data.message : 'Non è stato possibile completare l’operazione. Riprova.');
const date = (value) => new Date(value).toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit' });
const STATUS = { company_to_complete: 'Da completare', company_to_sign: 'Da firmare', studio_to_sign: 'In attesa della firma dello studio' };
const statusText = (item) => (item.status === 'signed' ? `Firmato il ${date(item.signedAt)}` : STATUS[item.status]);
const TODO = ['company_to_complete', 'company_to_sign'];
const openUrl = (url) => window.open(url, '_blank', 'noopener,noreferrer');

// Le rotte dei documenti ricevuti sono riservate al ruolo Azienda.
export const showsStudioDocuments = (user) => Boolean(user?.groups?.includes('Azienda'));

// Documenti inviati dallo Studio: nell'onboarding tutti, in Documenti azienda solo quelli da completare o firmare.
export default function StudioDocumentsToSign({ companyId, variant, onItemsChange = null }) {
  const [items, setItems] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [completing, setCompleting] = useState(null);
  const [session, setSession] = useState(null);
  const current = useRef(0);
  const pending = useRef(false);

  const load = useCallback(async () => {
    const generation = current.current;
    const result = await OnboardingService.received(companyId);
    if (generation === current.current) { setItems(result); onItemsChange?.(result); }
  }, [companyId, onItemsChange]);
  useEffect(() => {
    current.current += 1; const generation = current.current;
    setItems(null); setError(''); setBusy(false); pending.current = false; setCompleting(null); setSession(null);
    load().catch((failure) => { if (generation === current.current) setError(message(failure)); });
    return () => { current.current += 1; };
  }, [load]);

  const run = async (action) => {
    if (pending.current) return;
    pending.current = true; setBusy(true); setError('');
    const generation = current.current;
    const valid = () => generation === current.current;
    try { await action(valid); }
    catch (failure) {
      if (valid()) {
        setError(message(failure));
        if (failure.response?.status === 409) { setCompleting(null); await load().catch(() => {}); }
      }
    } finally { if (valid()) { pending.current = false; setBusy(false); } }
  };
  const missing = completing ? completing.item.companyFields.some((field) => field.required && !completing.input[field.key]?.trim()) : true;
  const preview = () => run(async () => {
    const blob = await OnboardingService.receivedPreview(companyId, completing.item.id, completing.input);
    openUrl(URL.createObjectURL(blob));
  });
  const complete = () => run(async (valid) => {
    await OnboardingService.complete(companyId, completing.item.id, completing.input);
    if (valid()) { setCompleting(null); await load(); }
  });

  const visible = variant === 'documents' ? (items || []).filter((item) => TODO.includes(item.status)) : items || [];
  if (variant === 'documents' && !visible.length && !error) return null;

  const list = <Stack spacing={2}>
    {error && <Alert severity="error" action={<Button disabled={busy} onClick={() => run(load)}>Riprova</Button>}>{error}</Alert>}
    {variant === 'onboarding' && items?.length === 0 && !error && <Typography variant="body2" color="text.secondary">Quando lo studio ti invierà un documento da firmare, lo troverai qui.</Typography>}
    {visible.map((item) => <Stack key={item.id} direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={1} sx={{ borderBottom: 1, borderColor: 'divider', pb: 2 }}>
      <Stack><Typography variant="subtitle2">{item.name}</Typography><Typography variant="body2" color="text.secondary">{statusText(item)}</Typography></Stack>
      <Stack direction="row" spacing={1}>
        {item.status === 'company_to_complete' && <Button variant="contained" disabled={busy} onClick={() => setCompleting({ item, input: {} })}>Compila</Button>}
        {item.status !== 'company_to_complete' && <Button disabled={busy} onClick={() => run(async (valid) => { const file = await OnboardingService.receivedFile(companyId, item.id); if (valid()) openUrl(file.url); })}>{item.status === 'signed' ? 'Scarica' : 'Apri'}</Button>}
        {item.status === 'company_to_sign' && <Button variant="contained" disabled={busy} onClick={() => run(async (valid) => { const result = await OnboardingService.initReceivedSignature(companyId, item.id); if (valid()) setSession({ ...result, name: item.name }); })}>Firma</Button>}
      </Stack>
    </Stack>)}
    {completing && <Dialog open fullWidth maxWidth="sm" onClose={() => { if (!busy) setCompleting(null); }}>
      <DialogTitle>{completing.item.name}</DialogTitle>
      <DialogContent><Stack spacing={2} sx={{ pt: 1 }}>
        <Typography variant="body2" color="text.secondary">Completa i dati richiesti dallo studio. Dopo la conferma potrai firmare il documento.</Typography>
        {completing.item.companyFields.map((field) => <TextField key={field.key} label={field.label} size="small" type={field.type === 'date' ? 'date' : 'text'} required={field.required} InputLabelProps={field.type === 'date' ? { shrink: true } : undefined} value={completing.input[field.key] || ''} disabled={busy} onChange={(event) => setCompleting((previous) => ({ ...previous, input: { ...previous.input, [field.key]: event.target.value } }))} fullWidth />)}
      </Stack></DialogContent>
      <DialogActions>
        <Button disabled={busy} onClick={() => setCompleting(null)}>Indietro</Button>
        <Button disabled={busy} onClick={preview}>Anteprima</Button>
        <Button variant="contained" disabled={busy || missing} onClick={complete}>Conferma</Button>
      </DialogActions>
    </Dialog>}
    {session && <Dialog open fullWidth maxWidth="sm"><DialogTitle>{session.name}</DialogTitle><DialogContent>
      <SignatureOtpPanel key={session.sessionId} active sessionId={session.sessionId} expiresAt={session.expiresAt} channel="email" destinationMasked={session.destinationMasked} companyId={Number(companyId)} executeSignature={OnboardingService.executeReceivedSignature} onCancel={() => setSession(null)} onSuccess={() => { setSession(null); run(load); }} />
    </DialogContent></Dialog>}
  </Stack>;

  if (variant === 'onboarding') return <Stack spacing={2}><Typography variant="h6">Deleghe</Typography>{list}</Stack>;
  return <Card sx={{ mb: 3 }}>
    <CardHeader title={`Da firmare per lo studio (${visible.length})`} action={<Button onClick={() => setExpanded((value) => !value)}>{expanded ? 'Chiudi' : 'Mostra'}</Button>} />
    <Collapse in={expanded}><CardContent><Box>{list}</Box></CardContent></Collapse>
  </Card>;
}

StudioDocumentsToSign.propTypes = {
  companyId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  variant: PropTypes.oneOf(['onboarding', 'documents']).isRequired,
  onItemsChange: PropTypes.func,
};
